/* eslint-disable camelcase */
import { useState } from 'react';
import {
  useDisclosure,
  Flex,
  Box,
  Button,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  useColorModeValue,
  Heading,
  Text,
  HStack,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Icon,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import {
  useGetAllSpecialitiesQuery,
  Order_By,
  useInsertSpecialtyOneMutation,
  useUpdateSpecilityByPkMutation,
  useDeleteSpecialtyByPkMutation,
} from 'src/utils/__generated__/graphql';
import { CustomModal } from './CustomModal';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

export const Specialities = () => {
  const toast = useToast();
  const {
    data: specialtyData,
    refetch: refetchSpecialities,
    loading,
  } = useGetAllSpecialitiesQuery({
    variables: {
      order_by: [{ name: Order_By.Asc }],
    },
  });

  const [insertSpecialty] = useInsertSpecialtyOneMutation({
    onCompleted: () => {
      refetchSpecialities();
      toast({
        title: 'Specialty added successfully',
        status: 'success',
        duration: 2000,
      });
    },
  });

  const [updateSpecialty] = useUpdateSpecilityByPkMutation({
    onCompleted: () => {
      refetchSpecialities();
      toast({
        title: 'Specialty updated successfully',
        status: 'success',
        duration: 2000,
      });
    },
  });

  const [deleteSpecialty] = useDeleteSpecialtyByPkMutation({
    onCompleted: () => {
      refetchSpecialities();
      toast({
        title: 'Specialty deleted successfully',
        status: 'success',
        duration: 2000,
      });
    },
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [specialtyName, setSpecialtyName] = useState('');
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(null);

  // Color mode values
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const handleSpecialtyNameChange = (value: string) => {
    setSpecialtyName(value);
  };

  const openEditModal = (id: React.SetStateAction<null>, name: React.SetStateAction<string>) => {
    setSelectedSpecialtyId(id);
    setSpecialtyName(name);
    onOpen();
  };

  const handleAddSpecialty = async () => {
    try {
      await insertSpecialty({
        variables: {
          object: { name: specialtyName },
        },
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error adding specialty',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleEditSpecialty = async () => {
    try {
      await updateSpecialty({
        variables: {
          pk_columns: { id: selectedSpecialtyId },
          _set: { name: specialtyName },
        },
      });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error updating specialty',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteSpecialty = async (id: any) => {
    try {
      await deleteSpecialty({
        variables: {
          id,
        },
      });
    } catch (error: any) {
      toast({
        title: 'Error deleting specialty',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const totalSpecialties = specialtyData?.specialty?.length || 0;

  return (
    <>
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Box>
          <Box>
            {/* Header Section */}
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'start', md: 'center' }}
              mb={6}
              gap={4}
            >
              <Box>
                <HStack mb={2}>
                  <Heading size="lg">Specialties</Heading>
                  <Badge colorScheme="brand" fontSize="md" borderRadius="full" px={3}>
                    {totalSpecialties}
                  </Badge>
                </HStack>
                <Text color={textColor}>Manage restaurant specialties and cuisines</Text>
              </Box>
              <HStack spacing={4}>
                <Tooltip label="Refresh data">
                  <IconButton
                    aria-label="Refresh"
                    icon={<Icon as={FiRefreshCw as any} />}
                    isLoading={loading}
                    onClick={() => refetchSpecialities()}
                    variant="ghost"
                  />
                </Tooltip>
                <Button
                  leftIcon={<Icon as={FiPlus as any} />}
                  colorScheme="brand"
                  onClick={() => openEditModal(null, '')}
                >
                  Add Specialty
                </Button>
              </HStack>
            </Flex>

            {/* Table Section */}
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead bg={useColorModeValue('gray.50', 'gray.800')}>
                  <Tr>
                    <Th>Specialty Name</Th>
                    <Th>Restaurants</Th>
                    <Th textAlign="right">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {specialtyData?.specialty?.map((specialty) => (
                    <Tr key={specialty.id} _hover={{ bg: hoverBg }} transition="background-color 0.2s">
                      <Td>
                        <Text fontWeight="medium">{specialty.name}</Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            (specialty?.restaurants_aggregate?.aggregate?.count ?? 0) > 0 ? 'green' : 'yellow'
                          }
                          borderRadius="full"
                        >
                          {specialty?.restaurants_aggregate?.aggregate?.count ?? 0} restaurants
                        </Badge>
                      </Td>
                      <Td textAlign="right">
                        <HStack spacing={2} justify="flex-end">
                          <Tooltip label="Edit specialty">
                            <IconButton
                              size="sm"
                              colorScheme="brand"
                              variant="ghost"
                              icon={<Icon as={FiEdit2 as any} />}
                              aria-label="Edit specialty"
                              onClick={() => openEditModal(specialty.id, specialty.name)}
                            />
                          </Tooltip>
                          <Tooltip label="Delete specialty">
                            <IconButton
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              icon={<Icon as={FiTrash2 as any} />}
                              aria-label="Delete specialty"
                              onClick={() => handleDeleteSpecialty(specialty.id)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      </MotionBox>

      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        selectedId={selectedSpecialtyId}
        name={specialtyName}
        handleEdit={handleEditSpecialty}
        handleAdd={handleAddSpecialty}
        handleNameChange={handleSpecialtyNameChange}
      />
    </>
  );
};
